import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import {
  ajustarRecompensa,
  atualizarPagamento,
  cancelarRecompensa,
  enviarComprovante,
  pagarRecompensa,
} from '../../../src/services/indicacoes';
import { parseValorReais, soDigitos, validarDocumentoOpcional } from '../../../src/lib/indicacoes/formulario';
import { ROTULO_TIPO_RECOMPENSA, formatarMoeda } from '../../../src/lib/indicacoes/rotulos';
import type { IndicacaoRecompensa, RecompensaTipo } from '../../../types';
import ModalBase from './ModalBase';
import { CLASSE_BOTAO_PRIMARIO, CLASSE_BOTAO_SECUNDARIO, CLASSE_INPUT, CLASSE_ROTULO } from './estilos';

export type AcaoRecompensa = { tipo: 'ajustar' | 'pagar' | 'cancelar'; rec: IndicacaoRecompensa };

const TAMANHO_MAXIMO = 10 * 1024 * 1024;
const TIPOS_ACEITOS = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const TITULOS = { ajustar: 'Ajustar recompensa', pagar: 'Marcar como paga', cancelar: 'Cancelar recompensa' };

interface Props {
  acao: AcaoRecompensa;
  onClose: () => void;
  onFeito: () => void;
}

const RecompensaAcaoModal: React.FC<Props> = ({ acao, onClose, onFeito }) => {
  const { addToast } = useToast();
  const { rec } = acao;
  const [tipo, setTipo] = useState<RecompensaTipo>(rec.tipo_recompensa ?? 'dinheiro');
  const [valor, setValor] = useState(rec.valor != null ? String(rec.valor).replace('.', ',') : '');
  const [motivo, setMotivo] = useState('');
  const [cpf, setCpf] = useState(rec.indicador_cpf_cnpj ?? '');
  const [pix, setPix] = useState(rec.indicador_chave_pix ?? '');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Quem não é cliente não tem fatura para receber desconto.
  const tiposPermitidos = (Object.keys(ROTULO_TIPO_RECOMPENSA) as RecompensaTipo[]).filter(
    t => !(t === 'desconto_fatura' && rec.tipo_indicador === 'nao_cliente'),
  );

  const executar = async () => {
    setSalvando(true);
    try {
      if (acao.tipo === 'ajustar') {
        const v = parseValorReais(valor);
        if (v === null) throw new Error('Informe um valor válido.');
        if (!motivo.trim()) throw new Error('Informe o motivo do ajuste.');
        await ajustarRecompensa(rec.id, tipo, v, motivo.trim());
      } else if (acao.tipo === 'cancelar') {
        if (!motivo.trim()) throw new Error('Informe o motivo do cancelamento.');
        await cancelarRecompensa(rec.id, motivo.trim());
      } else {
        if (rec.valor == null || rec.tipo_recompensa == null) throw new Error('Defina tipo e valor antes de pagar (botão Ajustar).');
        if (rec.tipo_recompensa === 'dinheiro') {
          const d = validarDocumentoOpcional(cpf);
          if (!d.ok) throw new Error(d.erro ?? 'CPF/CNPJ inválido.');
          if (!soDigitos(cpf) || !pix.trim()) throw new Error('Preencha CPF/CNPJ e chave PIX de quem recebe.');
          if (soDigitos(cpf) !== (rec.indicador_cpf_cnpj ?? '') || pix.trim() !== (rec.indicador_chave_pix ?? '')) {
            await atualizarPagamento(rec.pessoa_id, cpf, pix.trim());
          }
        }
        if (arquivo && !TIPOS_ACEITOS.includes(arquivo.type)) throw new Error('O comprovante precisa ser PDF ou imagem.');
        if (arquivo && arquivo.size > TAMANHO_MAXIMO) throw new Error('O comprovante passou de 10 MB.');
        const caminho = arquivo ? await enviarComprovante(rec.id, arquivo) : null;
        await pagarRecompensa(rec.id, caminho, observacao.trim() || null);
      }
      addToast(
        acao.tipo === 'pagar' ? 'Recompensa marcada como paga.' : acao.tipo === 'cancelar' ? 'Recompensa cancelada.' : 'Recompensa ajustada.',
        'success',
      );
      onFeito();
    } catch (e) {
      addToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase
      titulo={TITULOS[acao.tipo]}
      subtitulo={`${rec.indicador_nome} · indicou ${rec.indicado_nome}`}
      onClose={onClose}
      largura="md"
      rodape={
        <>
          <button onClick={onClose} className={CLASSE_BOTAO_SECUNDARIO} disabled={salvando}>Voltar</button>
          <button onClick={executar} className={CLASSE_BOTAO_PRIMARIO} disabled={salvando}>
            {salvando && <Loader2 size={15} className="animate-spin" />} Confirmar
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {acao.tipo === 'ajustar' && (
          <>
            <div>
              <label className={CLASSE_ROTULO}>Tipo</label>
              <select className={CLASSE_INPUT} value={tipo} onChange={e => setTipo(e.target.value as RecompensaTipo)}>
                {tiposPermitidos.map(t => (
                  <option key={t} value={t}>{ROTULO_TIPO_RECOMPENSA[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={CLASSE_ROTULO}>Valor (R$)</label>
              <input className={CLASSE_INPUT} value={valor} onChange={e => setValor(e.target.value)} placeholder="Ex.: 50,00" />
            </div>
          </>
        )}

        {acao.tipo === 'pagar' && (
          <>
            <p className="text-sm text-gray-700 dark:text-slate-200">
              {rec.tipo_recompensa ? ROTULO_TIPO_RECOMPENSA[rec.tipo_recompensa] : 'Tipo a definir'} · <b>{formatarMoeda(rec.valor)}</b>
            </p>
            {rec.tipo_recompensa === 'dinheiro' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className={CLASSE_ROTULO}>CPF/CNPJ de quem recebe</label>
                  <input className={CLASSE_INPUT} value={cpf} onChange={e => setCpf(e.target.value)} />
                </div>
                <div>
                  <label className={CLASSE_ROTULO}>Chave PIX</label>
                  <input className={CLASSE_INPUT} value={pix} onChange={e => setPix(e.target.value)} />
                </div>
              </div>
            )}
            <div>
              <label className={CLASSE_ROTULO}>Comprovante (opcional, PDF ou imagem até 10 MB)</label>
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={e => setArquivo(e.target.files?.[0] ?? null)}
                className="text-sm"
              />
            </div>
            <div>
              <label className={CLASSE_ROTULO}>Observação</label>
              <input className={CLASSE_INPUT} value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Opcional" />
            </div>
          </>
        )}

        {acao.tipo !== 'pagar' && (
          <div>
            <label className={CLASSE_ROTULO}>Motivo</label>
            <textarea className={CLASSE_INPUT} rows={2} value={motivo} onChange={e => setMotivo(e.target.value)} />
          </div>
        )}
      </div>
    </ModalBase>
  );
};

export default RecompensaAcaoModal;
